import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { OptimizedImage } from "@/components/ui/optimized-image"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { imageSrcSet } from "@/data/location-images"
import { cdnUrl } from "@/lib/cdn"
import type { SeoMeta } from "@/types"
import { LOCATIONS, LOCATION_SLUGS } from "@/data/directory/registry"
import { getAreaGuidesForLocation } from "@/data/areas/registry"
import type { AreaGuide } from "@/data/areas/types"

/* ------------------------------------------------------------------ */
/* Area card                                                           */
/* ------------------------------------------------------------------ */

function AreaCard({
  guide,
  locationSlug,
}: {
  guide: AreaGuide
  locationSlug: string
}) {
  return (
    <Link
      href={`/areas/${locationSlug}/${guide.slug}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-stone bg-white transition-all hover:border-stone/80 hover:shadow-md"
    >
      {guide.imageUrl && (
        <div className="h-40 overflow-hidden">
          <OptimizedImage
            src={cdnUrl(guide.imageUrl)}
            srcSet={imageSrcSet(guide.imageUrl)}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            alt={guide.name}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            width={800}
            height={396}
          />
        </div>
      )}
      <div className="flex-1 p-6">
        <h3 className="font-display text-xl font-bold text-charcoal">
          {guide.name}
        </h3>
        <p className="mt-1 text-sm italic text-slate">{guide.tagline}</p>

        <div className="mt-4 flex items-baseline gap-2">
          <span className="font-display text-lg font-bold text-charcoal">
            {guide.avgPrice}
          </span>
          <span className="text-xs text-warm-grey">avg. price</span>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {guide.bestFor.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-red/8 px-2.5 py-0.5 text-[11px] font-medium text-red"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {guide.propertyTypes.slice(0, 3).map((type) => (
            <span
              key={type}
              className="rounded-full bg-stone/50 px-2.5 py-0.5 text-[11px] font-medium text-slate"
            >
              {type}
            </span>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-stone px-6 py-3">
        <span className="text-sm font-semibold text-red transition-colors group-hover:text-deep-red">
          Read guide
        </span>
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className="size-4 text-red transition-transform group-hover:translate-x-1"
        >
          <path
            fillRule="evenodd"
            d="M3 10a.75.75 0 0 1 .75-.75h10.638L10.23 5.29a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.158-3.96H3.75A.75.75 0 0 1 3 10Z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    </Link>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function AreasLocation({
  meta,
  location,
}: {
  meta: SeoMeta
  location: string
}) {
  const locationData = LOCATIONS[location]
  if (!locationData) return null

  const guides = getAreaGuidesForLocation(location)
  const otherLocations = LOCATION_SLUGS.filter((s) => s !== location)

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        {/* Structured Data — ItemList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `${locationData.name} Area Guides`,
            description: meta.description,
            numberOfItems: guides.length,
            itemListElement: guides.map((g, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: {
                "@type": "Place",
                name: `${g.name}, ${locationData.name}`,
                description: g.tagline,
              },
            })),
          })}
        </script>
        {/* Structured Data — BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${new URL(meta.canonical).origin}/` },
              { "@type": "ListItem", position: 2, name: "Area Guides", item: `${new URL(meta.canonical).origin}/areas` },
              { "@type": "ListItem", position: 3, name: locationData.name },
            ],
          })}
        </script>
      </Head>

      {/* Breadcrumb */}
      <nav className="border-b border-stone px-6 py-3">
        <div className="mx-auto max-w-4xl">
          <ol className="flex items-center gap-2 text-xs text-warm-grey">
            <li>
              <Link href="/areas" className="hover:text-charcoal">
                Area Guides
              </Link>
            </li>
            <li>/</li>
            <li className="font-medium text-charcoal">{locationData.name}</li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 py-20 md:py-28">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Area Guides
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            {locationData.name} area guides<span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            {guides.length} neighbourhood guides with average prices, transport
            links, schools, and local insights.
          </p>

          {/* Other locations */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs text-warm-grey">Also available:</span>
            {otherLocations.map((slug) => (
              <Link
                key={slug}
                href={`/areas/${slug}`}
                className="rounded-full border border-stone bg-white px-3 py-1 text-xs font-medium text-slate transition-colors hover:border-red hover:text-red"
              >
                {LOCATIONS[slug]?.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Area cards */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {guides.map((guide) => (
              <AreaCard
                key={guide.slug}
                guide={guide}
                locationSlug={location}
              />
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Directory link */}
      <section className="border-t border-stone bg-cream px-6 py-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-2xl font-bold text-charcoal md:text-3xl">
            Looking for an estate agent in {locationData.name}<span className="text-red">?</span>
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-base text-slate">
            Browse and compare {locationData.name} estate agents in our directory.
            Reviews, specialisms, and contact details — all in one place.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              href={`/directory/${location}`}
              className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
            >
              {locationData.name} agent directory
            </Link>
            <Link
              href={`/best/${location}`}
              className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
            >
              Best agents in {locationData.name}
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl">
            Are you a {locationData.name} estate agent<span className="text-red">?</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-warm-grey">
            Turn your property listings into scroll-stopping video ads. Paste a
            listing, pick a style, and publish across every social platform in
            under a minute.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Request a demo
            </Link>
            <Link
              href="/how-it-works"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-6 py-3 text-base font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              See how it works
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
